import { Route } from '../types/route';
import { RouteMonthlyCheckin } from '../types/boarding';
import { Operation } from '../types/operation';

export interface PassengerCounts {
  passengers: number;
  reservationBoarding: number;
  reservationAlighting: number;
  qrBoarding: number;
  qrAlighting: number;
  journeys: number;
}

export interface PassengerStation {
  id: string;
  aliases: string[];
  name: string;
  order: number;
}

export const emptyPassengerCounts = (): PassengerCounts => ({
  passengers: 0, reservationBoarding: 0, reservationAlighting: 0,
  qrBoarding: 0, qrAlighting: 0, journeys: 0,
});

export const passengerKey = (date: string, routeId: string, stationName: string) =>
  JSON.stringify([date, routeId, stationName]);

export const koreanDate = (date: Date): string =>
  new Date(date.getTime() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);

export interface PassengerEntry extends PassengerCounts {
  date: string;
  routeId: string;
  stationName: string;
}

interface ArrivalTiming {
  delayMinutes?: number;
  status?: string;
}

export function summarizeArrivalTiming(records: ArrivalTiming[]) {
  let delayedRecords = 0;
  let earlyArrivalRecords = 0;
  let totalDelayMinutes = 0;
  let totalEarlyArrivalMinutes = 0;
  records.forEach(({ delayMinutes, status }) => {
    if (status === 'skipped' || status === 'pending' || status === 'missing' ||
      typeof delayMinutes !== 'number' || !Number.isFinite(delayMinutes)) return;
    if (delayMinutes > 0) { delayedRecords++; totalDelayMinutes += delayMinutes; }
    if (delayMinutes < 0) { earlyArrivalRecords++; totalEarlyArrivalMinutes += Math.abs(delayMinutes); }
  });
  return {
    delayedRecords, earlyArrivalRecords,
    averageDelayMinutes: delayedRecords ? totalDelayMinutes / delayedRecords : null,
    averageEarlyArrivalMinutes: earlyArrivalRecords ? totalEarlyArrivalMinutes / earlyArrivalRecords : null,
  };
}

export const formatAverageMinutes = (minutes: number | null): string =>
  minutes === null ? '-' : `${minutes.toFixed(1)}분`;

export function summarizeStations(date: string, routes: Route[], operations: Operation[], entries: PassengerEntry[]) {
  const summaries = new Map<string, PassengerCounts & {
    stationId: string; stationName: string; records: number; routes: number;
    passedRecords: number; normalRecords: number; timings: ArrivalTiming[]; routeIds: Set<string>;
  }>();
  const ensure = (name: string) => {
    if (!summaries.has(name)) summaries.set(name, { ...emptyPassengerCounts(), stationId: name,
      stationName: name, records: 0, routes: 0, passedRecords: 0, normalRecords: 0, timings: [], routeIds: new Set() });
    return summaries.get(name)!;
  };
  const operatingDay = new Date(`${date}T00:00:00Z`).getUTCDay() || 7;
  routes.forEach(route => {
    if (route.operatingDays && !route.operatingDays.includes(operatingDay)) return;
    route.stops.forEach(stop => { if (stop.stationName) ensure(stop.stationName).routeIds.add(route.id); });
  });
  operations.filter(operation => operation.operationDate === date).forEach(operation => {
    const logs = operation.stationLogs?.length ? operation.stationLogs.map(log => ({
      name: log.stationName, skipped: log.status === 'skipped', delayMinutes: log.delayMinutes,
      status: log.status === 'skipped' ? 'skipped' : log.actualArrivalTime ? 'arrived' : 'pending',
    })) : (operation.stopRecords || []).map(stop => ({ name: stop.stopName, skipped: stop.isSkipped,
      delayMinutes: undefined, status: stop.isSkipped ? 'skipped' : stop.arrivalTime ? 'arrived' : 'pending' }));
    logs.forEach(log => {
      const current = ensure(log.name || '정류장명 없음');
      current.routeIds.add(operation.routeId);
      current.records++;
      if (log.skipped) current.passedRecords++;
      // Count completed, non-delayed arrivals and passes; pending/unknown timing is excluded.
      if (log.skipped || (log.status === 'arrived' && typeof log.delayMinutes === 'number' &&
        Number.isFinite(log.delayMinutes) && log.delayMinutes <= 0)) current.normalRecords++;
      current.timings.push(log);
    });
  });
  entries.filter(entry => entry.date === date).forEach(entry => {
    const current = ensure(entry.stationName);
    current.routeIds.add(entry.routeId);
    (Object.keys(emptyPassengerCounts()) as (keyof PassengerCounts)[]).forEach(field => { current[field] += entry[field]; });
  });
  return Array.from(summaries.values(), ({ routeIds, timings, ...summary }) => ({
    ...summary, routes: routeIds.size, ...summarizeArrivalTiming(timings),
  }));
}

export function normalizeRoute(id: string, data: Record<string, any>): Route {
  return {
    ...data, id, name: data.name || id,
    departureTime: data.departureTime || data.departure_time || '',
    stops: (data.stops || []).map((stop: Record<string, any>) => ({
      stationId: String(stop.stationId ?? stop.station_id ?? ''),
      stationName: String(stop.stationName ?? stop.station_name ?? '').trim(),
      arrivalTime: stop.arrivalTime || stop.arrival_time || '',
      order: typeof stop.order === 'number' ? stop.order : 0,
    })).sort((a: { order: number }, b: { order: number }) => a.order - b.order),
  };
}

export function aggregateStationPassengers(
  documents: RouteMonthlyCheckin[], routes: Route[], stations: PassengerStation[], yearMonth: string,
) {
  const counts = new Map<string, PassengerCounts>();
  const totals = new Map<string, number>();
  const unmatched = new Map<string, number>();
  const routeMap = new Map(routes.map(route => [route.id, route]));
  const stationIds = new Map<string, string>();
  stations.forEach(station => station.aliases.forEach(id => stationIds.set(id, station.name)));
  let invalidTimestamps = 0;

  for (const document of documents) {
    // Remove only the month suffix: route IDs can contain underscores.
    const routeId = document.id.endsWith(`_${yearMonth}`)
      ? document.id.slice(0, -7) : document.id;
    const route = routeMap.get(routeId);
    if (!route) continue; // Also isolates production/test routes and administrator scope.
    const stops = route.stops || [];
    const names = new Set(stops.map(stop => stop.stationName));
    const ids = new Map(stops.filter(stop => stop.stationId).map(stop => [stop.stationId!, stop.stationName]));
    const resolve = (value: unknown): string | undefined => {
      const key = typeof value === 'string' ? value.trim() : typeof value === 'number' ? String(value) : '';
      if (!key) return undefined;
      return ids.get(key) || (names.has(key) ? key : undefined) || stationIds.get(key);
    };
    // Resolve once per route, not once per passenger.
    const hubs = stops.filter(stop => stop.stationName === '강서구청' || stop.stationName === '하단역');
    const namedHubs = hubs.filter(stop => route.name.includes(stop.stationName));
    const candidates = namedHubs.length ? namedHubs : hubs;
    const hub = new Set(candidates.map(stop => stop.stationName)).size === 1 ? candidates[0]?.stationName : undefined;

    for (const checkin of document.checkins || []) {
      const timestamp = typeof checkin.timestamp?.toDate === 'function' ? checkin.timestamp.toDate() : undefined;
      if (!timestamp || !Number.isFinite(timestamp.getTime())) { invalidTimestamps++; continue; }
      const date = koreanDate(timestamp);
      if (date.slice(0, 7).replace('-', '') !== yearMonth) continue;
      totals.set(date, (totals.get(date) || 0) + 1);
      const reservation = Object.prototype.hasOwnProperty.call(checkin, 'alighting_station') ||
        Object.prototype.hasOwnProperty.call(checkin, 'boarding_station');
      const commuting = typeof route.isCommuting === 'boolean' ? route.isCommuting :
        new Date(timestamp.getTime() + 9 * 60 * 60 * 1000).getUTCHours() < 12;
      const qrStation = resolve(checkin.station_id) || resolve(checkin.station_name);
      const boarding = reservation ? resolve(checkin.boarding_station) : commuting ? hub : qrStation;
      const alighting = reservation ? resolve(checkin.alighting_station) : commuting ? qrStation : hub;
      const touched = new Set<string>();
      for (const [stationName, field] of [
        [boarding, reservation ? 'reservationBoarding' : 'qrBoarding'],
        [alighting, reservation ? 'reservationAlighting' : 'qrAlighting'],
      ] as const) {
        if (!stationName) { unmatched.set(date, (unmatched.get(date) || 0) + 1); continue; }
        const key = passengerKey(date, routeId, stationName);
        const current = counts.get(key) || emptyPassengerCounts();
        current[field]++;
        current.passengers++;
        if (!touched.has(key)) current.journeys++;
        touched.add(key);
        counts.set(key, current);
      }
    }
  }
  return { counts, totals, unmatched, invalidTimestamps };
}
